/* eslint-disable no-console */
import axios from "axios";
import Cookies from "universal-cookie";
import * as Sentry from '@sentry/react'
import { SignJWT } from 'jose';
import { getAPIURL } from "../Helpers/apiUrl";
import { DEFAULT_TIMEZONE } from "../Helpers/Constants";
import { buildSentryExtra } from "../utils/sanitizeData";
import { safeError } from "../utils/logger";

const cookies = new Cookies();
const accessToken = cookies.get("accessToken");
const API_URL = process.env.REACT_APP_API_URL;
let key = process.env.REACT_APP_JWT_KEY;
let payload = {
  iss: process.env.REACT_APP_JWT_ISS,
};

// Secure JWT token generation function using jose library
async function generateToken() {
  try {
    const secret = new TextEncoder().encode(key);
    
    const token = await new SignJWT(payload)
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setExpirationTime('1m')
      .sign(secret);
    
    return token;
  } catch (error) {
    safeError("Error generating JWT token:", error);
    // Fallback to a simple string if JWT generation fails
    return `${payload.iss}_${Date.now()}`;
  }
}

export async function callApi({ url, method, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: method,
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result.data;
  } catch (error) {
    safeError("callApi error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    if (error && error.response) {
      return error.response;
    }
  }
}



export async function callPostApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  let storetimezone = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  if (body?.orderTimezone) {
    storetimezone = body?.orderTimezone;
  }
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "POST",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": storetimezone, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callPostApi error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    if (error && error.response) {
      return error.response;
    }
  }
}

export async function callPostApiv2(url, body, headers, notoken) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
    delete body?.selectedStore;
  }else if (body?.selectedStore){
    selectedStore = body?.selectedStore;
    delete body?.selectedStore;
  } else if (body?.zipcode){
    headers["zipcode"] = body?.zipcode;
    delete body?.zipcode;
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  if (notoken) {
    authHeader.authorization = "";
  }
  let storetimezone = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  if (body?.orderTimezone) {
    storetimezone = body?.orderTimezone;
  }
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "POST",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": storetimezone, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callPostApiv2 error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    throw error;
  }
}

export async function callGetApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "GET",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callGetApiv2 error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    if (error && error.response) {
      return error.response;
    }
  }
}

export async function callGetApiv2(url, body, headers) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "GET",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    throw error;
  }
}

export async function callPutApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "PUT",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callPutApi error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    if (error && error.response) {
      return error.response;
    }
  }
}

export async function callPutApiv2(url, body, headers) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "PUT",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callPutApiv2 error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    throw error;
  }
}

export async function callDeleteApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "DELETE",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callDeleteApi error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    if (error && error.response) {
      return error.response;
    }
  }
}

export async function callDeleteApiv2(url, body, headers) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken ? { authorization: accessToken } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "DELETE",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    safeError("callDeleteApiv2 error", error);
    const sentryExtra = buildSentryExtra({
      error: error?.response?.data,
      body,
      url,
      message: error?.response?.data?.message || "",
      generateTime: new Date().toLocaleString(),
    });
    Sentry.captureMessage(
      `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
      { extra: sentryExtra, level: 'warning' }
    );
    return error.response
  }
}
